import { cn } from '@/utils/tailwind-util';
import { CheckIcon } from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid/index';
import { createPortal } from 'react-dom';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import AppErrorFallback from '@/app/AppErrorFallback';
import { IconButton } from '@/components/Button';
import Placeholder from '@/components/Placeholder';
import ToggleButton from '@/components/ToggleButton';
import { useChatStore, useVenueManagement } from '@/features/chat';
import { useFetchClientListForPanel, useFetchUserDetail } from '@/services';

interface IProps {
  onClose: () => void;
}

const ClientMobileNav = ({ onClose }: IProps) => {
  const navigate = useNavigate();
  const user = useFetchUserDetail();
  const { data, isError, error, isLoading } = useFetchClientListForPanel();
  const [searchParams] = useSearchParams();
  const { clientId, venueId } = useParams();
  const { pathname } = useLocation();
  const rootPath = '/' + pathname.split('/')[1];

  const { venueIds, hasVenue } = useVenueManagement();

  if (isError) {
    return (
      <div>
        <AppErrorFallback error={error} />
      </div>
    );
  }

  const clientPanel = data?.content;
  const panelData =
    rootPath === '/task'
      ? clientPanel
      : clientPanel
          ?.map((client) => ({
            ...client,
            venues: client.venues.filter((venue) => venue.hasCleanierUser),
          }))
          .filter((client) => client.venues.length > 0)
          .filter((client) => {
            if (!searchParams.get('my')) return true;
            return client.venues.some((venue) =>
              user?.responsibleVenues?.some(
                (responsibleVenue) => responsibleVenue.id === venue.id
              )
            );
          }); // isActive관련해서 비활성화면 filter해야할지도 나중에?

  const handleTotalClick = () => {
    if (rootPath === '/task') return;
    navigate('/chat/all', {
      replace: true,
    });
    onClose();
  };
  const handleClientClick =
    ({ targetClientId, targetVenueId }) =>
    () => {
      if (
        (targetClientId === Number(clientId) && !venueId) ||
        rootPath === '/task'
      ) {
        const taskPath = `/task/client/${targetClientId}/venue/${targetVenueId}?type=periodic`;
        navigate(taskPath);
        onClose();
        return;
      }

      navigate(`/chat/client/${targetClientId}`);
      onClose();
    };

  const handleVenueClick =
    ({ targetClientId, targetVenueId }) =>
    () => {
      const taskPath = `/task/client/${targetClientId}/venue/${targetVenueId}?type=periodic`;
      const chatPath = `/chat/client/${targetClientId}/venue/${targetVenueId}`;
      const path = rootPath === '/task' ? taskPath : chatPath;
      navigate(path);
      onClose();
    };

  const handleToggler = () => {
    if (rootPath === '/task') {
      return;
    }
    const myOnly = searchParams.get('my') === 'true';
    if (myOnly) {
      searchParams.delete('my');
    } else {
      searchParams.set('my', 'true');
    }
    navigate(
      { pathname: `/chat/all`, search: searchParams.toString() },
      { replace: true }
    );
  };

  return createPortal(
    <>
      <div className="fixed top-0 left-0 h-screen w-screen z-[1000000] flex bg-black bg-opacity-60">
        <div className="inline-block absolute top-[1.625rem] right-6 p-2 bg-white shadow-lg rounded-[8px]">
          <IconButton
            size="md"
            label="close"
            iconElement={<XIcon className="text-black" />}
            onClick={onClose}
          />
        </div>
        {/* 닫는 버튼 */}
        <div className="h-full w-[256px] bg-white shadow-lg overflow-y-auto pt-6 px-4">
          <div className="flex flex-col gap-2">
            <button
              key="all-clients"
              className={cn(
                'w-full h-10 gap-1 flex items-center hover:bg-primary-50',
                location.pathname.includes('all') && 'bg-primary-50'
              )}
              onClick={handleTotalClick}
            >
              {location.pathname.includes('all') && <CheckIcon className="ml-6 w-5" />}
              <h4
                className={cn(
                  'truncate pl-1 text-gray-900 font-medium text-sm',
                  location.pathname.includes('all') && 'pl-1'
                )}
              >
                전체 고객사
              </h4>
            </button>
            <div
              className={cn(
                'w-full flex h-10 justify-between items-center pr-3',
                rootPath === '/task' && 'hidden'
              )}
            >
              <p className="pl-6 text-gray-500 font-normal text-sm">내 고객사</p>
              <ToggleButton
                id="my-only-toggler"
                isDisabled={!hasVenue || rootPath === 'task'}
                isChecked={searchParams.get('my') === 'true'}
                onChange={handleToggler}
                size="sx"
              />
            </div>
          </div>
          <div className="h-[1px] my-4 bg-gray-300" />
          {isLoading ? (
            <>
              {Array.from({ length: 10 }, (_, i) => (
                <Placeholder key={i} className="h-8 mb-1" />
              ))}
            </>
          ) : (
            <>
              {' '}
              {panelData?.map((client) => (
                <div key={client.id} className="pl-1 flex flex-col">
                  <button
                    className={cn(
                      'w-full flex items-center h-10 gap-1 hover:bg-primary-50',
                      client.id === Number(clientId) &&
                        !venueId &&
                        'bg-primary-100 hover:bg-primary-100'
                    )}
                    onClick={handleClientClick({
                      targetClientId: client.id,
                      targetVenueId: client.venues[0].id,
                    })}
                  >
                    <p
                      className={cn(
                        'truncate pl-1 text-gray-900 font-medium text-sm',
                        client.id === Number(clientId) && !venueId && 'pl-6'
                      )}
                    >
                      {client.name}
                    </p>
                  </button>
                  <div className="flex flex-col">
                    {client.venues
                      .filter((venue) => {
                        if (rootPath === 'chat') return venue.hasCleanierUser;
                        return true;
                      })
                      .filter((venue) => {
                        if (!searchParams.get('my')) return true;
                        return user?.responsibleVenues.find(
                          (responsibleVenue) => responsibleVenue.id === venue.id
                        );
                      })
                      .map((venue) => {
                        const isSelected =
                          (client.id === Number(clientId) &&
                            Number(venueId) === venue.id) ||
                          (client.id === Number(clientId) && !venueId);
                        return (
                          <button
                            key={venue.id}
                            className={cn(
                              'w-full h-10 flex items-center hover:bg-primary-50',
                              ((client.id === Number(clientId) && !venueId) ||
                                isSelected) &&
                                'bg-primary-50'
                            )}
                            onClick={handleVenueClick({
                              targetClientId: client.id,
                              targetVenueId: venue.id,
                            })}
                          >
                            {isSelected && <CheckIcon className="ml-6 w-5" />}
                            <p
                              className={cn('truncate pl-6 text-gray-500 font-normal text-sm', isSelected && 'pl-2')}
                            >
                              {venue.name}
                            </p>
                            {!venue.isActive && (
                              <div className="ml-2 py-1 px-2 bg-[#DADEE4]   text-[12px] align-bottom rounded-[4px] whitespace-nowrap">
                                비활성화
                              </div>
                            )}
                          </button>
                        );
                      })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </>,
    document.getElementById('modal-root') as HTMLElement
  );
};

export default ClientMobileNav;
